<template>
	<page-content-layout class="fraud" title="Bedrageritlarm">
		<button-group-view slot="tools" :actions="categoryActions" v-model="category" />
		<button-group-view slot="tools" :actions="severityActions" v-model="severity" />
		<button-group-view slot="tools" :actions="statusActions" v-model="status" />
		<new-table-view ref="table" :columns="columns" :items="filteredItems" :pagination="pagination" @itemClick="itemClick">
			<template #footer>
				<pagination-view style="padding: 10px;" :count="pagination.count" :page="pagination.page" @update:page="onPageChange" />
			</template>
		</new-table-view>
	</page-content-layout>
</template>

<script>
import NewTableView from '../views/NewTableView'
import SeverityBadgeView from '../views/SeverityBadgeView'
import StatusDotView from '../views/StatusDotView'
import FraudAlarmWindow from '../windows/FraudAlarmWindow'
import { ruleTitle } from '../fraud/rules'
import { bus } from '../../bus'

// Danish relative date for both past and future:
//   "I dag", "I går", "N dage siden" (past)
//   "I morgen", "om N dage" (future)
// Unparseable input echoes back as-is. Keeps the column visually
// uniform - no random "15.4.2026" mixed in with the relative ones.
function relativeDate(ts) {
	if (!ts) return '-';
	const d = new Date(ts);
	if (isNaN(d.getTime())) return String(ts);
	const startOfDay = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
	const diff = Math.floor((startOfDay(new Date()) - startOfDay(d)) / 86400000);
	if (diff === 0)  return 'I dag';
	if (diff === 1)  return 'I går';
	if (diff > 1)    return diff + ' dage siden';
	if (diff === -1) return 'I morgen';
	return 'om ' + Math.abs(diff) + ' dage';
}

// Pick the most-relevant date for an alarm - the date of the underlying
// event being flagged, NOT when the detector inserted the row. During a
// backfill every alarm.created is today, which made the column useless.
// Each detector's evidence carries the right pivot under a different
// key; we walk the candidates in priority order, falling back to the
// alarm's own created timestamp if nothing else lands.
function eventDate(item) {
	const ev = item && item.evidence ? item.evidence : {};
	return ev.from_date          // leave.* (period start)
		|| ev.leave_date         // leave.stamp_conflict, hours.* etc.
		|| ev.date               // hours.leave_overlap
		|| ev.occurred_at        // financial.after_hours_send
		|| ev.first_event        // financial.invoice_ping_pong
		|| ev.sent_at            // financial.invoice_disappeared/substituted
		|| ev.attempted_at       // financial.invoiced_without_reference
		|| ev.created            // order-flow financial alarms (order's own created)
		|| (item && item.created);
}

export default {
	data() {
		return {
			items: [],
			category: 'all',
			severity: 'all',
			status: 'open',
			pagination: { page: 1, index: null, count: 0 }
		};
	},
	components: {
		newTableView: NewTableView
	},
	computed: {
		categoryActions() {
			return [
				{ label: 'Alle',         value: 'all' },
				{ label: 'Økonomi',      value: 'financial' },
				{ label: 'Fravær',       value: 'leave' },
				{ label: 'Timer',        value: 'hours' }
			];
		},
		severityActions() {
			return [
				{ label: 'Alle',         value: 'all' },
				{ label: 'Info',         value: 'info' },
				{ label: 'Advarsel',     value: 'warn' },
				{ label: 'Kritisk',      value: 'critical' }
			];
		},
		statusActions() {
			return [
				{ label: 'Åben',         value: 'open' },
				{ label: 'Eskaleret',    value: 'escalated' },
				{ label: 'Afvist',       value: 'dismissed' }
			];
		},
		filteredItems() {
			// End of today (Europe/Copenhagen-aligned via local browser TZ).
			// Events dated AFTER this are hidden from the active queue but
			// stay in the DB - they re-appear automatically once the date
			// crosses into today (page reload or bus 'update' refetch).
			const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
			const todayMs  = todayEnd.getTime();

			let r = (this.items || []).filter(item => {
				const ts = eventDate(item);
				if (!ts) return true; // no date in evidence = always show
				const t = new Date(ts).getTime();
				if (!isFinite(t)) return true;
				return t <= todayMs;
			});

			if (this.category !== 'all') {
				r = r.filter(i => i.category === this.category);
			}
			if (this.severity !== 'all') {
				r = r.filter(i => i.severity === this.severity);
			}
			// Newest event first - same date as the Oprettet column. The API
			// returns rows ordered by alarm.created DESC, but during a
			// backfill that clusters everything at "I dag"; sorting by the
			// event date surfaces the most-recent real activity at the top.
			// Falls back to alarm.created when no event date in evidence.
			return r.slice().sort((a, b) => {
				const da = new Date(eventDate(a)).getTime() || 0;
				const db = new Date(eventDate(b)).getTime() || 0;
				return db - da;
			});
		},
		columns() {
			return [
				{
					label: 'Status',
					name: 'status',
					component: StatusDotView,
					weight: 2
				},
				{
					label: 'Alvorlighed',
					name: 'severity',
					component: SeverityBadgeView,
					weight: 2
				},
				{
					label: 'Oprettet',
					name: 'event_date',
					data: (item) => relativeDate(eventDate(item)),
					weight: 2
				},
				{
					label: 'Kategori',
					name: 'category',
					translation: { financial: 'Økonomi', leave: 'Fravær', hours: 'Timer' },
					weight: 1
				},
				{
					label: 'Regel',
					name: 'rule',
					data: (item) => ruleTitle(item.rule),
					weight: 3
				},
				{
					label: 'Resumé',
					name: ['evidence', 'summary'],
					weight: 4
				},
				{
					label: 'Aktør',
					name: 'actor_name',
					weight: 2
				},
				{
					label: 'Vedrører',
					name: 'about_employee_name',
					weight: 2
				}
			];
		}
	},
	watch: {
		status() { this.pagination = { ...this.pagination, page: 1 }; this.update(); }
	},
	mounted() {
		this.update();
		bus.$on('update', this._onBusUpdate);
	},
	beforeDestroy() {
		bus.$off('update', this._onBusUpdate);
	},
	methods: {
		_onBusUpdate() { this.update(); },
		onPageChange(page) {
			this.pagination = { ...this.pagination, page };
			this.update();
		},
		async update() {
			this.items = null;
			const params = { _page: this.pagination.page };
			if (this.status) params.status = this.status;

			try {
				const { data } = await this.axios.get('/fraud/alarms/', { params });
				this.items = data._items || [];
				if (data._pagination) this.pagination = data._pagination;
			} catch (err) {
				console.error('Failed to load fraud alarms', err);
				this.items = [];
			}
		},
		async itemClick(item) {
			const ent = (await this.axios.get(`/fraud/alarms/${item.id}/`)).data;
			this.$store.state.windowStack.push(FraudAlarmWindow, { ent });
		}
	}
};
</script>

<style lang="scss" scoped>
.fraud {
	/* Inherits page-content-layout styles. */
}

/* Compress the three filter button-groups in the page-tools slot - the
   default 30px horizontal padding per action makes 11 buttons across
   three groups eat ~960px. Tightened to 12px / 32px line-height which
   keeps them instantly-clickable but saves ~570px of horizontal real
   estate at the top of the page. ::v-deep is required to pierce
   button-group-view's scoped styles. */
::v-deep .button-group-view {
	& > .action {
		padding: 0 12px;
		line-height: 30px;
		font-size: 12px;
		letter-spacing: 0.2px;
	}
}
</style>
